import { FC, useMemo } from "react";
import Table from "rsuite/Table";
import { useTranslation } from "react-i18next";

import type { KeywordsTableCellProps } from "../../types";
import { KEYWORDS_TABLE_STYLES } from "../../constants";
import { getExactMatchKeywordRegExp } from "src/features/SearchKeywords/utils";

const { Cell } = Table;

type RelatedCellProps = KeywordsTableCellProps & {
  search?: Search.Data | Search.CreationData;
};

const hasAnyRelatedKeyword = (
  children: KeywordsTableCellProps["rowData"][],
  regexp: RegExp,
): boolean => {
  for (const child of children) {
    const childValue = child?.value || "";
    const isExactMatch = regexp.test(childValue.toLowerCase());

    if (!isExactMatch) return true;

    if (child?.children?.length) {
      if (hasAnyRelatedKeyword(child.children, regexp)) return true;
    }
  }

  return false;
};

export const RelatedCell: FC<RelatedCellProps> = ({
  rowData: keywordData = {},
  dataKey,
  search,
  ...props
}) => {
  const { t } = useTranslation();

  const isRelated = useMemo<boolean>(() => {
    if (!search || !keywordData?.value) return false;

    const regexp = getExactMatchKeywordRegExp(search);

    if (keywordData?.children?.length) {
      return hasAnyRelatedKeyword(keywordData.children, regexp);
    }

    const isExactMatch = regexp.test(keywordData.value.toLowerCase());
    return !isExactMatch;
  }, [search, keywordData?.value, keywordData?.children]);

  return (
    <Cell
      {...props}
      dataKey={dataKey}
      height={KEYWORDS_TABLE_STYLES.cellHeight}
    >
      {isRelated
        ? t("component.keywords_table.label.related_keywords")
        : t("component.keywords_table.label.exact_match_keywords")}
    </Cell>
  );
};
